import { useState, useEffect, useCallback } from "react";
import { api } from "../api";

function fmt(n) { return Number(n || 0).toFixed(2); }

function hoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const USER_COLORS = ["#4cc9f0", "#f72585", "#4caf50", "#ffc107", "#7209b7", "#4361ee"];
function userColor(idx) { return USER_COLORS[idx % USER_COLORS.length]; }

// Columnas del cierre — orden igual al PDF de Morpheus
const COLS = [
  { key: "efectivo",   label: "Efectivo",   color: "#4caf50"  },
  { key: "deposito",   label: "Depósito",   color: "#4cc9f0"  },
  { key: "tarjeta",    label: "Tarjeta",    color: "#7209b7"  },
  { key: "cheque",     label: "Cheque",     color: "#4361ee"  },
  { key: "devolucion", label: "Devol.",     color: "#e63946"  },
  { key: "credito",    label: "Crédito",    color: "#ffc107"  },
  { key: "anulado",    label: "Anul.",      color: "#555"     },
  { key: "total",      label: "Total",      color: "#fff"     },
];

// ─── Celda monto ──────────────────────────────────────────────────────────────
function Monto({ value, color }) {
  const v = Number(value || 0);
  if (v === 0) return <span style={{ color: "#333", fontFamily: "monospace", fontSize: 12 }}>0.00</span>;
  return <span style={{ color, fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>{fmt(v)}</span>;
}

// ─── Fila de factura ──────────────────────────────────────────────────────────
function FilaFactura({ f }) {
  const esAnulada = f.estado === "A";
  return (
    <tr style={{ borderBottom: "1px solid #1a1a2e", opacity: esAnulada ? 0.4 : 1 }}>
      <td style={td}>
        <span style={{ color: "#555", fontSize: 10 }}>{f.fecha}</span>
      </td>
      <td style={td}>
        <span style={{ color: "#aaa", fontFamily: "monospace", fontSize: 11 }}>
          {f.factura?.replace("001-010-000", "")}
        </span>
      </td>
      <td style={{ ...td, maxWidth: 220 }}>
        <div style={{ color: "#ccc", fontSize: 12,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {f.cliente}
        </div>
      </td>
      {COLS.map(c => (
        <td key={c.key} style={{ ...td, textAlign: "right" }}>
          <Monto value={f[c.key]} color={c.color} />
        </td>
      ))}
    </tr>
  );
}

// ─── Bloque por usuario ───────────────────────────────────────────────────────
function BloqueUsuario({ usuario, idx, expandido, onToggle }) {
  const color = userColor(idx);
  const t = usuario.totales;

  // KPIs no cero para mostrar en header
  const kpisActivos = COLS.filter(c => c.key !== "total" && t[c.key] > 0);

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Header clickeable */}
      <div onClick={onToggle} style={{
        display: "flex", alignItems: "center", gap: 12,
        background: "#1e1e2e",
        borderRadius: expandido ? "10px 10px 0 0" : 10,
        padding: "12px 16px", cursor: "pointer",
        borderLeft: `4px solid ${color}`, transition: "all .15s",
      }}>
        {/* Avatar */}
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: `${color}22`, border: `1.5px solid ${color}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color, fontWeight: 700, fontSize: 12, flexShrink: 0,
        }}>
          {usuario.nombre.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase()}
        </div>

        {/* Nombre */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{usuario.nombre}</div>
          <div style={{ color: "#555", fontSize: 10, marginTop: 1 }}>
            {t.n_facturas} factura{t.n_facturas !== 1 ? "s" : ""}
          </div>
        </div>

        {/* KPIs resumen */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          {kpisActivos.map(c => (
            <div key={c.key} style={{ textAlign: "right" }}>
              <div style={{ color: "#555", fontSize: 9, textTransform: "uppercase" }}>{c.label}</div>
              <div style={{ color: c.color, fontFamily: "monospace",
                fontWeight: 700, fontSize: 13 }}>${fmt(t[c.key])}</div>
            </div>
          ))}
          <div style={{ textAlign: "right", marginLeft: 8,
            paddingLeft: 14, borderLeft: "1px solid #2a2a3e" }}>
            <div style={{ color: "#555", fontSize: 9, textTransform: "uppercase" }}>Total</div>
            <div style={{ color, fontFamily: "monospace",
              fontWeight: 700, fontSize: 15 }}>${fmt(t.total)}</div>
          </div>
          <div style={{ color: "#444", fontSize: 16 }}>{expandido ? "▲" : "▼"}</div>
        </div>
      </div>

      {/* Tabla detalle */}
      {expandido && (
        <div style={{ background: "#13131f", borderRadius: "0 0 10px 10px",
          border: "1px solid #1e1e2e", borderTop: "none", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead>
              <tr style={{ background: "#0f0f1a" }}>
                {["Fecha", "N° Fact.", "Cliente", ...COLS.map(c => c.label)].map((h, i) => (
                  <th key={h} style={{
                    ...th,
                    textAlign: i >= 3 ? "right" : "left",
                    color: i >= 3 ? COLS[i - 3]?.color || "#aaa" : "#555",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuario.facturas.map(f => <FilaFactura key={f.factura} f={f} />)}
            </tbody>
            <tfoot>
              <tr style={{ background: "#1e1e2e", borderTop: "2px solid #2a2a3e" }}>
                <td colSpan={2} style={{ ...td, color: "#555", fontSize: 11 }}>
                  SUBTOTAL · {t.n_facturas} fact.
                </td>
                <td style={td} />
                {COLS.map(c => (
                  <td key={c.key} style={{ ...td, textAlign: "right" }}>
                    <strong style={{ color: c.color, fontFamily: "monospace",
                      fontSize: 13 }}>${fmt(t[c.key] || 0)}</strong>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CierreCaja() {
  const [fecha,      setFecha]      = useState(hoy());
  const [ptovta,     setPtovta]     = useState(1);
  const [data,       setData]       = useState(null);
  const [cargando,   setCargando]   = useState(false);
  const [expandidos, setExpandidos] = useState({});

  const cargar = useCallback(() => {
    setCargando(true);
    api.get(`/ventas/cierre-caja?fecha=${fecha}&ptovta=${ptovta}`)
      .then(d => {
        setData(d);
        const exp = {};
        (d.usuarios || []).forEach(u => { exp[u.usercod] = true; });
        setExpandidos(exp);
      })
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [fecha, ptovta]);

  useEffect(() => { cargar(); }, [cargar]);

  function toggleUser(ucod) {
    setExpandidos(prev => ({ ...prev, [ucod]: !prev[ucod] }));
  }
  function toggleAll(expand) {
    const exp = {};
    (data?.usuarios || []).forEach(u => { exp[u.usercod] = expand; });
    setExpandidos(exp);
  }

  const t = data?.totales || {};
  const hayDatos = data?.usuarios?.length > 0;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: "#e0e0e0" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ color: "#fff", margin: 0, fontSize: 20, fontWeight: 700 }}>
            Cierre de Caja
          </h2>
          <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>
            Por usuario · Pto. Venta {ptovta}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input type="date" value={fecha}
            onChange={e => setFecha(e.target.value)}
            style={inputStyle} />
          <select value={ptovta} onChange={e => setPtovta(Number(e.target.value))}
            style={{ ...inputStyle, width: 120 }}>
            <option value={1}>PtoVta 1</option>
          </select>
          <button onClick={cargar} disabled={cargando} style={{
            background: "#4361ee", border: "none", color: "#fff",
            borderRadius: 8, padding: "8px 18px", fontSize: 12,
            fontWeight: 600, cursor: cargando ? "wait" : "pointer",
          }}>
            {cargando ? "Cargando..." : "🔄 Actualizar"}
          </button>
        </div>
      </div>

      {/* ── KPIs totales ── */}
      {hayDatos && (
        <div style={{ display: "grid",
          gridTemplateColumns: `repeat(${COLS.length}, 1fr)`,
          gap: 10, marginBottom: 20 }}>
          {COLS.map(c => (
            <div key={c.key} style={{
              background: "#1e1e2e", borderRadius: 10,
              padding: "12px 16px", borderTop: `3px solid ${c.color}`,
            }}>
              <div style={{ color: "#555", fontSize: 10,
                textTransform: "uppercase", letterSpacing: 1 }}>{c.label}</div>
              <div style={{ color: c.color, fontFamily: "monospace",
                fontWeight: 700, fontSize: 18, marginTop: 4 }}>
                ${fmt(t[c.key] || 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Controles ── */}
      {hayDatos && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12,
          justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ color: "#555", fontSize: 12 }}>
            {t.n_facturas} facturas · {data.usuarios.length} usuarios · {fecha}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => toggleAll(true)}  style={btnSecStyle}>▼ Expandir todo</button>
            <button onClick={() => toggleAll(false)} style={btnSecStyle}>▲ Colapsar todo</button>
          </div>
        </div>
      )}

      {/* ── Sin datos ── */}
      {!cargando && data?.usuarios?.length === 0 && (
        <div style={{ color: "#555", textAlign: "center", padding: 60, fontSize: 14 }}>
          📭 Sin facturas para {fecha}
        </div>
      )}

      {cargando && (
        <div style={{ color: "#555", textAlign: "center", padding: 60 }}>Cargando...</div>
      )}

      {/* ── Bloques por usuario ── */}
      {!cargando && (data?.usuarios || []).map((u, idx) => (
        <BloqueUsuario
          key={u.usercod}
          usuario={u}
          idx={idx}
          expandido={!!expandidos[u.usercod]}
          onToggle={() => toggleUser(u.usercod)}
        />
      ))}

      {/* ── Total general al pie ── */}
      {hayDatos && (
        <div style={{
          background: "#1e1e2e", borderRadius: 10,
          padding: "14px 20px", marginTop: 8,
          border: "1px solid #2a2a3e",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ color: "#555", fontSize: 12, fontWeight: 700 }}>
            TOTAL GENERAL
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {COLS.filter(c => (t[c.key] || 0) > 0).map(c => (
              <div key={c.key} style={{ textAlign: "right" }}>
                <div style={{ color: "#444", fontSize: 9,
                  textTransform: "uppercase" }}>{c.label}</div>
                <div style={{ color: c.color, fontFamily: "monospace",
                  fontWeight: 700, fontSize: 15 }}>${fmt(t[c.key])}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────
const inputStyle = {
  background: "#1e1e2e", border: "1px solid #2a2a3e", borderRadius: 8,
  color: "#fff", padding: "8px 12px", fontSize: 12, outline: "none",
};

const btnSecStyle = {
  background: "#1e1e2e", border: "1px solid #2a2a3e", color: "#555",
  borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
};

const th = {
  padding: "8px 12px", fontSize: 10, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.5,
  color: "#555", whiteSpace: "nowrap",
};

const td = {
  padding: "8px 12px", verticalAlign: "middle",
};